// ---------------------------------------------------------------------------
// routeColorNotes — derives per-turn "color" captions for the turn-by-turn
// directions list. Real-drive note (#27): "add some color or interest to the
// directions that have to do with community or AI-search-informed content."
//
// A route already carries the named features it passes — parks, water,
// landmarks, historic places, nature spots. Where one of those names genuinely
// appears in a turn's road name or instruction text, we attach a colored note
// to that turn. The match is honest by construction: a note only lands on a
// turn whose road is actually named for the feature (a parkway, a lake road,
// a historic main street). Nothing is invented for a turn we can't tie to a
// real feature on the route.
//
// The note text starts as a plain feature line ("Passing Rockefeller Park").
// DirectionsView upgrades it asynchronously with an AI-written sentence from
// the poi-fact edge function when the network allows.
// ---------------------------------------------------------------------------

import type { RouteStep } from "@/api/routing";

export type ColorNoteKind = "park" | "water" | "landmark" | "historic" | "nature";

export interface ColorNote {
  /** Index into the route's `steps` array this note is attached to. */
  stepIndex: number;
  kind: ColorNoteKind;
  /** The matched feature name, e.g. "Rockefeller State Park Preserve". */
  name: string;
  /** Plain caption shown before — or instead of — the AI sentence. */
  plain: string;
}

interface FeatureNames {
  parkNames?: string[];
  waterNames?: string[];
  landmarkNames?: string[];
  historicNames?: string[];
  natureNames?: string[];
}

// Words too generic to anchor a match — a step road containing only "Park" or
// "River" tells us nothing about WHICH feature it is, so an anchor word made
// of one of these alone would match the wrong road.
const STOPWORDS = new Set([
  "park", "river", "lake", "creek", "pond", "trail", "preserve", "state",
  "national", "historic", "district", "the", "of", "and", "mount", "mt",
  "reservoir", "garden", "gardens", "wood", "woods", "hill", "hills",
  "road", "street", "avenue", "drive", "lane", "way", "route", "county",
  "north", "south", "east", "west", "old", "new", "upper", "lower",
]);

/** The most distinctive token of a feature name — the longest non-stopword
 *  word. Lets "Rockefeller State Park Preserve" still match a step that runs
 *  on "Rockefeller Road" even though the full name never appears verbatim. */
function anchorWord(name: string): string | null {
  const words = name.split(/[\s\-/]+/).filter(Boolean);
  const candidates = words
    .filter((w) => w.length >= 4 && !STOPWORDS.has(w.toLowerCase()))
    .sort((a, b) => b.length - a.length);
  return candidates[0] ?? null;
}

function haystack(step: RouteStep): string {
  return `${step.instruction ?? ""} ${step.roadName ?? ""}`.toLowerCase();
}

// Water first — a river/parkway pairing is the most evocative; landmarks last
// because their names least often appear in road names.
const KIND_ORDER: ColorNoteKind[] = ["water", "park", "nature", "historic", "landmark"];

const PLAIN_VERB: Record<ColorNoteKind, string> = {
  water: "Along",
  park: "Passing",
  nature: "Passing",
  historic: "Passing",
  landmark: "Passing",
};

/** poi-fact category hint for each kind — steers the AI sentence. */
export const POI_FACT_CATEGORY: Record<ColorNoteKind, string> = {
  water: "river",
  park: "park",
  nature: "nature",
  historic: "historic",
  landmark: "landmark",
};

/** Build at most `max` per-turn color notes for a route. Returns notes sorted
 *  by step order, at most one per step and one per feature. */
export function buildRouteColorNotes(
  steps: RouteStep[],
  features: FeatureNames | undefined,
  max = 5,
): ColorNote[] {
  if (!features || steps.length === 0) return [];

  const byKind: Record<ColorNoteKind, string[]> = {
    water: features.waterNames ?? [],
    park: features.parkNames ?? [],
    nature: features.natureNames ?? [],
    historic: features.historicNames ?? [],
    landmark: features.landmarkNames ?? [],
  };

  const haystacks = steps.map(haystack);
  const notes: ColorNote[] = [];
  const usedSteps = new Set<number>();
  const usedNames = new Set<string>();

  for (const kind of KIND_ORDER) {
    for (const name of byKind[kind]) {
      if (!name) continue;
      const lower = name.toLowerCase();
      if (usedNames.has(lower)) continue;
      const anchor = anchorWord(name);
      const anchorLower = anchor ? anchor.toLowerCase() : null;
      let matchIdx = -1;
      for (let i = 0; i < haystacks.length; i++) {
        if (usedSteps.has(i)) continue;
        const h = haystacks[i];
        if (h.includes(lower) || (anchorLower && h.includes(anchorLower))) {
          matchIdx = i;
          break;
        }
      }
      if (matchIdx < 0) continue;
      usedSteps.add(matchIdx);
      usedNames.add(lower);
      notes.push({
        stepIndex: matchIdx,
        kind,
        name,
        plain: `${PLAIN_VERB[kind]} ${name}`,
      });
      if (notes.length >= max) {
        return notes.sort((a, b) => a.stepIndex - b.stepIndex);
      }
    }
  }
  return notes.sort((a, b) => a.stepIndex - b.stepIndex);
}
